import { cn } from '@/lib/cn';
import { Button } from '@heidi/design-system/button';
import { TypographyP2, TypographyP2Bold } from '@heidi/design-system/typography';
import { Sparkles } from 'lucide-react';
import { type RefObject } from 'react';
import { useIntl } from 'react-intl';

import { voiceAssistantMessages, type CalloutPlacement } from '@heidi/agents';
import { advanceVoiceGuideFromUi } from './useVoiceGuideRunner';

type VoiceHighlightCalloutProps = {
  calloutRef: RefObject<HTMLDivElement>;
  placement: CalloutPlacement | null;
  title?: string;
  body?: string | null;
  showContinue?: boolean;
};

export const VoiceHighlightCallout = ({
  calloutRef,
  placement,
  title,
  body,
  showContinue,
}: VoiceHighlightCalloutProps) => {
  const intl = useIntl();

  return (
    <div
      ref={calloutRef}
      data-testid="voice-highlight-callout"
      className={cn(
        'pointer-events-auto absolute left-0 top-0 z-50 w-[min(18rem,calc(100vw-1.5rem))]',
        'overflow-hidden rounded-2xl border border-border/70 bg-surface shadow-xl',
        'transition-[transform,opacity] duration-300 ease-out-quart',
        'motion-reduce:transition-none',
        'motion-safe:duration-300 motion-safe:animate-in motion-safe:fade-in-0 motion-safe:zoom-in-95',
        placement ? 'opacity-100' : 'opacity-0'
      )}
      style={
        placement
          ? {
              transform: `translate3d(${placement.left}px, ${placement.top}px, 0)`,
            }
          : undefined
      }
    >
      <div className="border-s-2 border-border-focus p-3">
        {placement && placement.side !== 'fallback' ? (
          <span
            aria-hidden
            data-testid="voice-highlight-callout-caret"
            className={cn(
              'absolute size-2.5 rotate-45 border-border/70 bg-surface',
              placement.side === 'below' &&
                '-top-1.5 start-1/2 -translate-x-1/2 border-s border-t',
              placement.side === 'above' &&
                '-bottom-1.5 start-1/2 -translate-x-1/2 border-b border-e',
              placement.side === 'right' && '-start-1.5 top-4 border-b border-s',
              placement.side === 'left' && '-end-1.5 top-4 border-e border-t'
            )}
          />
        ) : null}
        <div className="mb-1.5 flex items-center gap-1.5">
          <Sparkles className="size-3.5 shrink-0 text-text-tertiary" aria-hidden />
          <span className="text-[10px] font-medium uppercase tracking-wide text-text-tertiary">
            {intl.formatMessage(voiceAssistantMessages.highlightEyebrow)}
          </span>
        </div>
        {title ? (
          <TypographyP2Bold
            className="text-text-primary"
            data-testid="voice-highlight-callout-title"
          >
            {title}
          </TypographyP2Bold>
        ) : null}
        {body ? (
          <TypographyP2
            className={cn('text-pretty text-text-secondary', title ? 'mt-1' : null)}
          >
            {body}
          </TypographyP2>
        ) : null}
        {showContinue ? (
          <div className="mt-3 flex justify-end">
            <Button
              variant="default"
              size="sm"
              data-testid="voice-guide-continue"
              onClick={() => advanceVoiceGuideFromUi()}
            >
              {intl.formatMessage(voiceAssistantMessages.guideContinue)}
            </Button>
          </div>
        ) : null}
      </div>
    </div>
  );
};
